import { useDatabase } from '../composables/database'
import { v4 as uuidv4 } from 'uuid'
import { TransactionsRow, TransactionsRows } from '../types/database'
import { jsDateToSqlite } from './helpers'

type InventoryTransactionsModelReturn = {
  /**Creates a new transaction record */
  newTransaction: (
    data: Omit<TransactionsRow, 'uuid' | 'created_at' | 'successful_sync'>
  ) => Promise<string>
  /** Returns transactions after a date. Date is a sqlite datetime(). */
  transactionsToSync: (
    /**sqlite date */
    date: string | Date,
    /**Optional task ids */
    stores_id?: string | number
  ) => Promise<TransactionsRows>
  clearTransactions: () => Promise<void>
  flagSuccess: (uuids: string[]) => Promise<void>
  get: (uuid: string) => Promise<TransactionsRow | undefined>
  getByTaskId: (taskID: number) => Promise<TransactionsRows>
  removeTransaction: (uuid: TransactionsRow['uuid']) => Promise<void>
}
export const InventoryTransactionsModel = (): InventoryTransactionsModelReturn => {
  const { query, run } = useDatabase()
  const queryTransaction = async (sql: string) => {
    return await query(sql)
      .then((transactions: TransactionsRows | void | null) => {
        if (transactions) {
          return transactions
        } else {
          return []
        }
      })
      .catch((error) => {
        console.log(error)
        return []
      })
  }

  //Creates new transaction. Transaction represents the transfer of a single item.
  const newTransaction = async (
    data: Omit<TransactionsRow, 'uuid' | 'created_at' | 'successful_sync'>
  ) => {
    const sqlData: Omit<TransactionsRow, 'created_at' | 'successful_sync'> = {
      uuid: uuidv4(),
      ...data,
    }
    const sql = `
        INSERT INTO inventory_transactions (
            uuid, transaction_type, from_entity, to_entity,  
            part_number, quantity, tasks_id, stores_id, collections_id,
            orders_id, reason, reason_id, comment,
            created_at
            )
        VALUES (
            ?, ?, ?, ?, 
            ?, ?, ?, ?, ?,
            ?, ?, ?, ?,
            datetime('now', 'localtime') 
            );
        `
    const values = [
      sqlData.uuid,
      sqlData.transaction_type,
      sqlData.from_entity,
      sqlData.to_entity,
      sqlData.part_number,
      sqlData.quantity,
      sqlData.tasks_id,
      sqlData.stores_id,
      sqlData.collections_id,
      sqlData.orders_id,
      sqlData.reason,
      sqlData.reason_id,
      sqlData.comment,
    ]
    await run(sql, values)
      .catch((error) => console.log(`Error inserting new transaction record: ${error}`))
      .then(async () => {
        console.log(sqlData)
        console.log('post Transaction write')
        await query('SELECT * FROM inventory_transactions')
          .catch(() => {
            //
          })
          .then((res) => {
            console.log(res)
          })
      })
    return sqlData.uuid
  }

  /**
   * Grab the transactions either
   * a) past a specified date (last synced), or
   * b) tasks that are not currently associated to a store that is actively logging time
   */
  const transactionsToSync = async (date: string | Date, stores_id?: string | number) => {
    if (date instanceof Date) {
      date = jsDateToSqlite(date)
    }
    //If there are any task ids, conver them to string represenation of array and build or statement
    const excludeStoreIdQuery = stores_id
      ? ` OR (stores_id != ${Number(stores_id)} AND successful_sync = 0)`
      : 'OR (stores_id IS NOT NULL AND successful_sync = 0)'

    const sql = `
        SELECT * FROM inventory_transactions
        WHERE created_at >= '${date}'
        AND stores_id IS NULL
        ${excludeStoreIdQuery}
        ;
        `
    console.log(sql)
    return await queryTransaction(sql).catch((error) => {
      console.log(error)
      return []
    })
  }

  const flagSuccess = async (uuids: string[]) => {
    const sql = `
    UPDATE inventory_transactions
    SET successful_sync = 1
    WHERE uuid IN (${uuids.map(() => '?').join(',')});
    `
    await run(sql, [...uuids]).catch((error) => {
      console.log(`Error updating inventory transactions: ${error}`)
    })
  }

  const getByTaskId = async (taskId: number) => {
    const sql = `
      SELECT * FROM inventory_transactions WHERE tasks_id = ${Number(taskId)};
    `
    return await queryTransaction(sql).catch((error) => {
      console.log(error)
      return []
    })
  }
  const get = async (uuid: string) => {
    const sql = `
    SELECT * FROM inventory_transactions WHERE uuid = '${uuid}';
    `
    return await query(sql).then((res: void | TransactionsRows | null) => {
      if (res) {
        return res[0]
      }
    })
  }
  const removeTransaction = async (uuid: TransactionsRow['uuid']) => {
    const sql = `
    DELETE FROM inventory_transactions
    WHERE uuid = ?
    `
    const values = [uuid]
    await run(sql, values).catch((error) =>
      console.log(`Error deleting transaction record "${uuid}": ${error}`)
    )
  }
  const clearTransactions = async () => {
    const sql = 'DELETE FROM inventory_transactions'
    await run(sql, []).catch((error) =>
      console.log(`Error deleting inventory transactions: ${error}`)
    )
  }
  return {
    newTransaction,
    transactionsToSync,
    clearTransactions,
    removeTransaction,
    flagSuccess,
    getByTaskId,
    get,
  }
}
